import React, { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { X } from 'lucide-react'
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { api } from '@/lib/api'
import { useAnalyticsFilters } from '@/contexts/AnalyticsFiltersContext'
import type { ScorecardKind } from './ScorecardTable'

/**
 * Compare modal (P3.b) — overlays weekly time series across 2–4 selected
 * scorecard entities (developers / brokers / architects).
 *
 * Charts:
 *   1. Units sold per week
 *   2. Cumulative sell-through (%)
 *   3. Avg €/m² per week (weekly trimmed average on sold units)
 */

interface ScorecardTimeSeriesPoint {
  weekStart: string
  unitsSold: number
  unitsListed: number
  cumulativeSellThroughPct: number | null
  avgSqmPrice: number | null
}

interface ScorecardTimeSeriesRow {
  id: number
  name: string
  points: ScorecardTimeSeriesPoint[]
}

interface ScorecardTimeSeriesResponse {
  kind: ScorecardKind
  ids: number[]
  weeks: number
  series: ScorecardTimeSeriesRow[]
}

interface Props {
  kind: ScorecardKind
  ids: number[]
  onClose: () => void
}

// Fixed palette (max 4 simultaneous entities).
const SERIES_COLORS = ['#2563eb', '#dc2626', '#059669', '#d97706']

const TITLE_BY_KIND: Record<ScorecardKind, string> = {
  developer: 'Developer comparison',
  broker: 'Broker comparison',
  architect: 'Architect comparison',
}

function formatWeek(iso: string): string {
  try {
    const d = new Date(iso)
    if (isNaN(d.getTime())) return iso
    return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' })
  } catch {
    return iso
  }
}

function formatEuro(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—'
  return `€${Math.round(n).toLocaleString('en-US')}`
}

export function ScorecardCompareModal({ kind, ids, onClose }: Props) {
  const { queryParams } = useAnalyticsFilters()
  const sortedIds = useMemo(() => [...ids].sort((a, b) => a - b), [ids])
  const params = useMemo(
    () => ({ ...queryParams, ids: sortedIds.join(',') }),
    [queryParams, sortedIds],
  )

  const { data, isLoading, error } = useQuery<ScorecardTimeSeriesResponse>({
    queryKey: ['analytics', 'scorecard-time-series', kind, sortedIds, queryParams],
    queryFn: async () => {
      const res = await api.get(`/analytics/scorecards/${kind}/time-series`, {
        params,
      })
      return res.data as ScorecardTimeSeriesResponse
    },
    enabled: sortedIds.length >= 2 && sortedIds.length <= 4,
    staleTime: 5 * 60 * 1000,
  })

  // Pivot the series into a single array of week-indexed points keyed by entity name.
  const chartRows = useMemo(() => {
    if (!data?.series.length) return []
    const weeks = data.series[0].points.map((p) => p.weekStart)
    return weeks.map((weekStart, idx) => {
      const row: Record<string, string | number | null> = { weekStart }
      for (const s of data.series) {
        const pt = s.points[idx]
        row[`${s.name}_sold`] = pt?.unitsSold ?? 0
        row[`${s.name}_sellThrough`] = pt?.cumulativeSellThroughPct ?? null
        row[`${s.name}_avgSqm`] = pt?.avgSqmPrice ?? null
      }
      return row
    })
  }, [data])

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-secondary-100 px-5 py-3">
          <div>
            <h2 className="text-base font-semibold text-secondary-900">
              {TITLE_BY_KIND[kind]}
            </h2>
            <p className="text-xs text-secondary-500">
              Last {data?.weeks ?? 26} weeks · honors the active filter bar.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-secondary-500 hover:bg-secondary-100"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {error ? (
          <div className="p-6 text-sm text-red-600">
            Failed to load comparison data: {(error as Error).message}
          </div>
        ) : isLoading || !data ? (
          <div className="p-6 text-sm text-secondary-500">Loading comparison…</div>
        ) : data.series.length === 0 ? (
          <div className="p-6 text-sm text-secondary-500">
            No activity in this window for the selected entities.
          </div>
        ) : (
          <div className="space-y-6 p-5">
            <CompareChart
              title="Units sold per week"
              subtitle="Weekly units that transitioned to sold (sales_date)."
              data={chartRows}
              series={data.series}
              suffixKey="_sold"
              valueFormatter={(v) =>
                v == null || !Number.isFinite(v) ? '—' : String(Math.round(v))
              }
            />

            <CompareChart
              title="Cumulative sell-through (%)"
              subtitle="Running sold ÷ running listed, computed within the visible window."
              data={chartRows}
              series={data.series}
              suffixKey="_sellThrough"
              valueFormatter={(v) =>
                v == null || !Number.isFinite(v) ? '—' : `${v.toFixed(1)}%`
              }
              domain={[0, 100]}
            />

            <CompareChart
              title="Average €/m² (sold units, trimmed)"
              subtitle="Weekly average of outlier-trimmed €/m² on sold inventory."
              data={chartRows}
              series={data.series}
              suffixKey="_avgSqm"
              valueFormatter={formatEuro}
            />
          </div>
        )}
      </div>
    </div>
  )
}

interface CompareChartProps {
  title: string
  subtitle: string
  data: Array<Record<string, string | number | null>>
  series: ScorecardTimeSeriesRow[]
  suffixKey: '_sold' | '_sellThrough' | '_avgSqm'
  valueFormatter: (v: number | null) => string
  domain?: [number, number]
}

function CompareChart({
  title,
  subtitle,
  data,
  series,
  suffixKey,
  valueFormatter,
  domain,
}: CompareChartProps) {
  return (
    <div>
      <div className="mb-1">
        <h3 className="text-sm font-semibold text-secondary-900">{title}</h3>
        <p className="text-xs text-secondary-500">{subtitle}</p>
      </div>
      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={data} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="weekStart"
            tick={{ fontSize: 11 }}
            tickFormatter={formatWeek}
          />
          <YAxis
            tick={{ fontSize: 11 }}
            domain={domain}
            tickFormatter={(v) => valueFormatter(v as number)}
            width={70}
          />
          <Tooltip
            labelFormatter={formatWeek}
            formatter={(value: number | null) => valueFormatter(value ?? null)}
          />
          <Legend />
          {series.map((s, i) => (
            <Line
              key={s.id}
              type="monotone"
              dataKey={`${s.name}${suffixKey}`}
              name={s.name}
              stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
              strokeWidth={2}
              dot={false}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}
